import { useSyncExternalStore } from 'react';

/**
 * A single shared "tick" driving every auto-advancing property-card carousel
 * on the page, so cards in the same grid advance in lockstep instead of each
 * running its own independent `setInterval`. Per-card timers drift apart over
 * time — most visibly the moment any one card is hovered (pausing just that
 * card) and then un-hovered (its timer restarts fresh from that instant,
 * permanently offset from every other card's timer from then on). Deriving
 * every card's advance from one shared clock means a paused card simply skips
 * ticks while hovered and rejoins the same rhythm on the very next one.
 *
 * One real `setInterval` backs every subscriber (lazily started on the first
 * subscriber, cleared when the last unsubscribes) — this is NOT one interval
 * per card.
 */
const INTERVAL_MS = 3200;

let tick = 0;
let intervalId: number | null = null;
const listeners = new Set<() => void>();

function ensureRunning() {
  if (intervalId !== null) return;
  intervalId = window.setInterval(() => {
    tick += 1;
    listeners.forEach((listener) => listener());
  }, INTERVAL_MS);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  ensureRunning();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
      tick = 0;
    }
  };
}

function getSnapshot(): number {
  return tick;
}

const NOOP_SUBSCRIBE = () => () => {};
const getDisabledSnapshot = () => 0;

/**
 * Returns a number that increments every `INTERVAL_MS`, shared across every
 * component calling this hook. Pass `enabled = false` for carousels that
 * don't auto-advance at all (single image, reduced motion) so they don't
 * needlessly keep the shared interval alive.
 */
export function useCarouselClock(enabled: boolean): number {
  return useSyncExternalStore(
    enabled ? subscribe : NOOP_SUBSCRIBE,
    enabled ? getSnapshot : getDisabledSnapshot,
    getDisabledSnapshot,
  );
}
